/**
 * <cba-search> — the search panel under the header (sections/predictive-search;
 * plan 6.1k: was the section's inline script).
 *
 *   <cba-search data-suggest-url="{{ routes.predictive_search_url }}"
 *     data-search-url="{{ routes.search_url }}" data-error="…">
 *     <div data-cba-search-overlay></div>
 *     <div id="cba-search" data-cba-search role="dialog" inert>
 *       … [data-cba-search-input role="combobox"] … [data-cba-search-results]
 *       … [role="listbox"] [role="option"] … [data-cba-search-products] …</div>
 *   </cba-search>
 *
 * - The header's search toggles ([data-cba-search-toggle], delegated, so a
 *   reloaded header keeps working) open and close it: links to /search that
 *   act as buttons here (role, Space). Open, the toggle shows an X and is the
 *   panel's close control (Leo, 2026-10-10: an X beside the field would read
 *   as "clear the field"). Escape and a click on the overlay close it too,
 *   and focus returns to the toggle. One overlay at a time: opening
 *   announces cba:overlay:open, and another overlay's announcement closes
 *   it. Open, the page scroll is locked (html only, DESIGN-RULES §6).
 * - Tab moves through the panel and the toggle, round and round: the close
 *   control is part of the loop (shell audit S4.2; before, focus was held
 *   inside the panel and the X could not be reached). As Dawn's
 *   details-modal, whose trap holds its close button. A modal sheet opened
 *   over the panel (quick add from a result) keeps its own focus.
 * - The field is a combobox over the list beside it (WAI-ARIA APG, list
 *   autocomplete with manual selection; Dawn's predictive-search): Arrow
 *   down and up move through the suggestions while focus stays in the
 *   field (aria-activedescendant), Enter opens the one marked, and with
 *   none marked it submits the search. The product cards are not options
 *   (an option cannot hold buttons); they are reached with Tab.
 * - The panel sits right under the header group, measured when it opens and
 *   on resize while it is open.
 * - Empty field: the server-rendered popular searches, and the recently
 *   viewed products: the ids the product pages remembered
 *   (assets/cba-recently-viewed.js), rendered fresh by the store through
 *   /search?q=id:… with this section (DESIGN-PLAN 7.G4, shell audit S6).
 * - Typing: the section is fetched from the predictive search URL 250 ms after
 *   the last keystroke, products and query suggestions only, six of each at
 *   most.
 *   Each keystroke aborts the request before it, and an answer for an older
 *   query is dropped. While it loads the results are aria-busy; the status
 *   region then says how many results there are, or that there are none;
 *   a request that fails clears the old results and says so (S4.4, S4.6).
 */
if (!customElements.get('cba-search')) {
  class CbaSearch extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-cba-search-overlay]');
      this.dropdown = this.querySelector('[data-cba-search]');
      this.input = this.querySelector('[data-cba-search-input]');
      this.results = this.querySelector('[data-cba-search-results]');
      if (!this.overlay || !this.dropdown) return;
      this.opener = null;
      this.lockedScroll = false;
      this.pending = null;
      this.abort = new AbortController();
      const { signal } = this.abort;

      /* Measured when it opens (open()) and on resize while open; not at
         load, where moving the hidden panel after the first paint counted as
         a layout shift (CLS 0.024 on desktop pages, found in plan 6.1). */
      window.addEventListener('resize', () => { if (this.isOpen()) this.syncHeaderOffset(); }, { signal });

      /* The header's search toggles open and close the panel (magnifier ↔ X). */
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cba-search-toggle]');
        if (!btn) return;
        /* A link to /search, which is where it goes without JavaScript. */
        e.preventDefault();
        if (this.isOpen()) this.close(true);
        else this.open(btn);
      }, { signal });
      this.overlay.addEventListener('click', () => this.close(true), { signal });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) { this.close(true); return; }
        /* Here the toggles are buttons, and a button answers Space too. */
        if (e.key === ' ' && e.target instanceof Element && e.target.matches('[data-cba-search-toggle]')) { e.preventDefault(); e.target.click(); return; }
        if (e.key === 'Tab' && this.isOpen()) this.keepFocus(e);
      }, { signal });
      this.setExpanded(false);
      /* Another overlay is taking over: focus goes there, not back here. */
      document.addEventListener('cba:overlay:open', (e) => { if (e.detail !== 'search') this.close(false); }, { signal });

      /* The server-rendered empty state, kept before the recently viewed
         cards go in, so every return to it starts from the same HTML. */
      this.liquidInitialHTML = this.results ? this.results.innerHTML : null;
      window.addEventListener('pageshow', (e) => { if (e.persisted && this.isOpen()) this.showInactiveState(); }, { signal });
      /* Recently viewed loads when the panel opens, not with every page; a
         pointer or focus on a search toggle starts it a moment early. */
      ['pointerenter', 'focusin'].forEach((type) => document.addEventListener(type, (e) => {
        if (e.target instanceof Element && e.target.closest('[data-cba-search-toggle]')) this.populateRecentlyViewed(true);
      }, { capture: true, signal }));

      if (this.input && this.results) {
        /* The combobox keys; the caret keeps Home, End, left and right. */
        this.input.addEventListener('keydown', (e) => {
          if (e.isComposing) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const options = this.options();
            if (!options.length) return;
            e.preventDefault();
            const at = options.indexOf(this.activeOption());
            const next = e.key === 'ArrowDown' ? (at + 1) % options.length : (at <= 0 ? options.length - 1 : at - 1);
            this.setActiveOption(options[next]);
          } else if (e.key === 'Enter') {
            const marked = this.activeOption();
            const link = marked && marked.querySelector('a');
            /* With none marked the form submits the search. */
            if (link) { e.preventDefault(); link.click(); }
          }
        }, { signal });
        this.input.addEventListener('input', (e) => {
          clearTimeout(this.debounce);
          if (this.pending) { this.pending.abort(); this.pending = null; }
          this.setActiveOption(null);
          const q = e.target.value.trim();
          if (q.length === 0) { this.showInactiveState(); return; }
          /* Busy from the first key, while the clicks settle and it loads. */
          if (this.results) this.results.setAttribute('aria-busy', 'true');
          this.debounce = setTimeout(() => this.fetchSection(q), 250);
        }, { signal });
      }
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      clearTimeout(this.debounce);
      if (this.pending) this.pending.abort();
      if (this.lockedScroll) document.documentElement.removeAttribute('data-cba-drawer-open');
    }

    isOpen() { return this.dropdown.classList.contains('is-open'); }

    setExpanded(open) {
      document.querySelectorAll('[data-cba-search-toggle]').forEach((t) => {
        t.setAttribute('role', 'button');
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    /* The toggle that shows: the header has one per row, phone and computer. */
    visibleToggle() {
      return [...document.querySelectorAll('[data-cba-search-toggle]')].find((t) => t.checkVisibility()) || null;
    }

    /* Tab goes round the panel and its close control, the header's toggle. */
    keepFocus(e) {
      /* A modal sheet over the panel keeps its own focus: the browser holds
         it there, and the panel behind is inert meanwhile. */
      if (document.querySelector('dialog:modal')) return;
      const active = document.activeElement;
      const inside = [...this.dropdown.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.tabIndex >= 0 && el.checkVisibility() && !el.closest('[inert]'));
      if (!inside.length) return;
      const toggle = this.visibleToggle();
      const first = inside[0];
      const last = inside[inside.length - 1];
      let to = null;
      if (active === toggle) to = e.shiftKey ? last : first;
      else if (!this.dropdown.contains(active)) to = first;
      else if (!e.shiftKey && active === last) to = toggle || first;
      else if (e.shiftKey && active === first) to = toggle || last;
      if (!to) return;
      e.preventDefault();
      to.focus();
    }

    options() { return this.results ? [...this.results.querySelectorAll('[role="option"]')] : []; }

    activeOption() { return this.results ? this.results.querySelector('[role="option"][aria-selected="true"]') : null; }

    /* The marked suggestion: focus stays in the field, and the field points
       at it (aria-activedescendant). */
    setActiveOption(option) {
      if (!this.input) return;
      this.options().forEach((o) => o.setAttribute('aria-selected', o === option ? 'true' : 'false'));
      if (option && option.id) {
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    /* After every render, open and close: nothing marked, and the field
       says whether a list of suggestions is showing. */
    syncCombobox() {
      if (!this.input) return;
      this.setActiveOption(null);
      this.input.setAttribute('aria-expanded', this.isOpen() && this.options().length ? 'true' : 'false');
    }

    /* The bottom of the header group (announcement bars and the header,
       stacked): the panel and the overlay start there. */
    syncHeaderOffset() {
      const sectionEl = this.dropdown.closest('[id^="shopify-section-"]');
      if (!sectionEl) return;
      let maxBottom = 0;
      for (let prev = sectionEl.previousElementSibling; prev; prev = prev.previousElementSibling) {
        if (prev.id && prev.id.indexOf('shopify-section-') === 0) {
          maxBottom = Math.max(maxBottom, prev.getBoundingClientRect().bottom);
        }
      }
      if (maxBottom > 0) {
        this.dropdown.style.setProperty('--cba-drawer-top', maxBottom + 'px');
        this.overlay.style.top = maxBottom + 'px';
      }
    }

    open(trigger) {
      if (this.isOpen()) return;
      this.opener = trigger || null;
      /* One overlay at a time: the menu, cart and shop menu close themselves. */
      document.dispatchEvent(new CustomEvent('cba:overlay:open', { detail: 'search' }));
      this.syncHeaderOffset();
      /* Re-read localStorage on every open, so recently viewed stays fresh. */
      if (!this.input || !this.input.value) this.showInactiveState();
      this.dropdown.inert = false;
      this.overlay.classList.add('is-open');
      this.dropdown.classList.add('is-open');
      this.dropdown.setAttribute('aria-hidden', 'false');
      this.setExpanded(true);
      /* Scroll lock shared with the other drawers; release only a lock this
         panel took. */
      const root = document.documentElement;
      if (!root.hasAttribute('data-cba-drawer-open')) { root.setAttribute('data-cba-drawer-open', '1'); this.lockedScroll = true; }
      this.syncCombobox();
      /* Focus in the same click that opened it: an iPhone shows the
         keyboard only then (S4.8). Once more after the slide, in case the
         panel was not focusable yet. */
      if (this.input) this.input.focus({ preventScroll: true });
      setTimeout(() => { if (this.input && this.isOpen() && document.activeElement !== this.input) this.input.focus(); }, 200);
    }

    /* returnFocus: false when another overlay is taking over. */
    close(returnFocus) {
      if (!this.isOpen()) return;
      this.overlay.classList.remove('is-open');
      this.dropdown.classList.remove('is-open');
      this.dropdown.setAttribute('aria-hidden', 'true');
      this.setExpanded(false);
      if (this.lockedScroll) { document.documentElement.removeAttribute('data-cba-drawer-open'); this.lockedScroll = false; }
      this.dropdown.inert = true;
      this.syncCombobox();
      if (returnFocus && this.opener && document.contains(this.opener)) this.opener.focus();
    }

    /* Recently viewed: the remembered ids, rendered fresh by the store with
       this section (search keeps no order; the section restores it). With
       none, or no answer, the server's card stays. One request per change
       of the list; a later search wins over it. */
    populateRecentlyViewed(prefetch) {
      let ids = [];
      try { ids = JSON.parse(localStorage.getItem('cba_recently_viewed_ids') || '[]'); } catch (e) { /* blocked or broken: keep the card */ }
      ids = (Array.isArray(ids) ? ids : []).filter((id) => /^\d+$/.test(String(id))).slice(0, 6);
      if (!ids.length) return;
      const key = ids.join(',');
      if (this.recentKey === key) { if (this.recentHTML && !prefetch) this.putRecent(this.recentHTML); return; }
      this.recentKey = key;
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', ids.map((id) => 'id:' + id).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[unavailable_products]', 'last');
      url.searchParams.set('section_id', 'predictive-search');
      fetch(url)
        .then((r) => { if (!r.ok) throw new Error('recently viewed ' + r.status); return r.text(); })
        .then((html) => {
          const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cba-search-products]');
          if (!list || !list.children.length) return;
          this.recentHTML = list.innerHTML;
          if (this.isOpen()) this.putRecent(this.recentHTML);
        })
        .catch(() => { this.recentKey = null; /* the server's card stays; the next open tries again */ });
    }

    /* Only into the empty state: a search may have started meanwhile. */
    putRecent(html) {
      if (this.input && this.input.value.trim()) return;
      const holder = this.dropdown.querySelector('[data-cba-search-products]');
      if (!holder) return;
      holder.innerHTML = html;
    }

    /* The status region says it (WCAG 4.1.3); emptied first, so the same
       words twice are announced twice. */
    announce(text) {
      const status = this.querySelector('[data-cba-search-status]');
      if (!status) return;
      status.textContent = '';
      if (text) setTimeout(() => { status.textContent = text; }, 50);
    }

    showInactiveState() {
      if (this.results && this.liquidInitialHTML !== null) this.results.innerHTML = this.liquidInitialHTML;
      if (this.results) this.results.removeAttribute('aria-busy');
      this.announce('');
      this.populateRecentlyViewed();
      this.syncCombobox();
    }

    /* The predictive_search object only fills from the suggest URL, not from
       the search page. */
    fetchSection(q) {
      const url = new URL(this.dataset.suggestUrl, window.location.origin);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('q', q);
      /* Products and query suggestions only, six of each at most (S4.7):
         the limit is per type, or the suggestions would take product places. */
      url.searchParams.set('resources[type]', 'product,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      if (this.pending) this.pending.abort();
      const request = this.pending = new AbortController();
      if (this.results) this.results.setAttribute('aria-busy', 'true');
      fetch(url, { signal: request.signal })
        .then((r) => { if (!r.ok) throw new Error('search ' + r.status); return r.text(); })
        .then((html) => {
          if (request !== this.pending || this.input.value.trim() !== q) return;
          this.pending = null;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const next = doc.querySelector('[data-cba-search-results]');
          if (next && this.results) {
            this.results.innerHTML = next.innerHTML;
            this.results.removeAttribute('aria-busy');
            const summary = this.results.querySelector('[data-cba-search-summary]');
            this.announce(summary ? summary.textContent.trim() : '');
            this.syncCombobox();
          }
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          if (request !== this.pending) return;
          this.pending = null;
          /* Not the last query's results under a new one (S4.4). */
          if (this.results) {
            const message = document.createElement('p');
            message.className = 'cba-field-error';
            message.textContent = this.dataset.error || '';
            this.results.replaceChildren(message);
            this.results.removeAttribute('aria-busy');
            this.syncCombobox();
          }
          this.announce(this.dataset.error || '');
        });
    }
  }

  customElements.define('cba-search', CbaSearch);
}
